import { useEffect, useCallback, useRef, useState } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListNode, ListItemNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { $getRoot, $insertNodes } from 'lexical'
import DOMPurify from 'dompurify'
import ToolbarPlugin from './ToolbarPlugin'
import './rich-text-input.scss'

const theme = {
  paragraph: 'editor-paragraph',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
  },
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
  },
  list: {
    ul: 'editor-list-ul',
    ol: 'editor-list-ol',
    listitem: 'editor-listitem',
  },
  quote: 'editor-quote',
  link: 'editor-link',
}

const editorNodes = [ListNode, ListItemNode, LinkNode, HeadingNode, QuoteNode]

function LoadInitialContentPlugin({ initialHtml }) {
  const [editor] = useLexicalComposerContext()
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!initialHtml || hasLoadedRef.current) return
    hasLoadedRef.current = true
    editor.update(() => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(DOMPurify.sanitize(initialHtml), 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      const root = $getRoot()
      root.clear()
      $insertNodes(nodes)
    })
  }, [initialHtml, editor])

  return null
}

function ReadOnlyPlugin({ isReadOnly }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    editor.setEditable(!isReadOnly)
  }, [editor, isReadOnly])

  return null
}

function MaxLengthPlugin({ maxLength, onHtmlLengthChange }) {
  const [editor] = useLexicalComposerContext()
  const lastValidStateRef = useRef(null)
  const isRevertingRef = useRef(false)

  useEffect(() => {
    if (!maxLength) return

    editor.read(() => {
      const html = $generateHtmlFromNodes(editor)
      const sanitizedHtml = DOMPurify.sanitize(html || '')
      const htmlLength = sanitizedHtml.trim().length
      onHtmlLengthChange(htmlLength)
      if (htmlLength <= maxLength) {
        lastValidStateRef.current = editor.getEditorState()
      }
    })

    const unregisterUpdate = editor.registerUpdateListener(({ editorState, prevEditorState, tags }) => {
      if (tags.has('max-length-revert') || isRevertingRef.current) return

      let htmlLength = 0
      editor.read(() => {
        const html = $generateHtmlFromNodes(editor)
        const sanitizedHtml = DOMPurify.sanitize(html || '')
        htmlLength = sanitizedHtml.trim().length
      })

      onHtmlLengthChange(htmlLength)

      if (htmlLength <= maxLength) {
        lastValidStateRef.current = editorState
      } else {
        const stateToRestore = lastValidStateRef.current || prevEditorState
        if (stateToRestore) {
          isRevertingRef.current = true
          editor.setEditorState(stateToRestore, { tag: 'max-length-revert' })
          editor.read(() => {
            const restoredHtml = $generateHtmlFromNodes(editor)
            const restoredSanitized = DOMPurify.sanitize(restoredHtml || '')
            onHtmlLengthChange(restoredSanitized.trim().length)
          })
          isRevertingRef.current = false
        }
      }
    })

    return () => {
      unregisterUpdate()
    }
  }, [editor, maxLength, onHtmlLengthChange])

  return null
}

const RichTextInput = (props) => {
  const { value, onChangeValue, isReadOnly, hasError, maxLength } = props
  const [htmlLength, setHtmlLength] = useState(0)

  const handleChange = useCallback(
    (_editorState, editor) => {
      editor.read(() => {
        const html = $generateHtmlFromNodes(editor)
        const sanitized = DOMPurify.sanitize(html || '')
        onChangeValue(sanitized)
      })
    },
    [onChangeValue],
  )

  const isAtLimit = maxLength && htmlLength >= maxLength

  const initialConfig = {
    namespace: 'RichTextInput',
    theme,
    nodes: editorNodes,
    editable: !isReadOnly,
    onError: (error) => console.error('Lexical error:', error),
  }

  return (
    <div className={`rich-text-input ${hasError ? 'rich-text-error' : ''}`}>
      <LexicalComposer initialConfig={initialConfig}>
        {!isReadOnly && <ToolbarPlugin />}
        <div className="editor-container">
          <RichTextPlugin contentEditable={<ContentEditable className="editor-content" />} ErrorBoundary={LexicalErrorBoundary} />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <OnChangePlugin onChange={handleChange} />
        <LoadInitialContentPlugin initialHtml={value} />
        <ReadOnlyPlugin isReadOnly={isReadOnly} />
        {maxLength && <MaxLengthPlugin maxLength={maxLength} onHtmlLengthChange={setHtmlLength} />}
      </LexicalComposer>
      {maxLength && !isReadOnly && (
        <div className={`char-count ${isAtLimit ? 'char-count-error' : ''}`}>
          {htmlLength}/{maxLength}
        </div>
      )}
    </div>
  )
}

export default RichTextInput

